import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ErrorMessage } from '@hookform/error-message';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody
} from 'reactstrap';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import FlexSelect from 'components/Inputs/FlexSelect';

import { useListLeadProcesses } from 'views/CRM/LeadProcess/hooks/useListLeadProcesses';
import { useListSalesProcesses } from 'views/CRM/SalesProcess/hooks/useListSalesProcesses';
import { useListProtocolProcesses } from 'views/CRM/ProtocolProcess/hooks/useListProtocolProcesses';

import RecordTypeService from '../service';
import { ContainerForm } from 'views/ProtocolConfiguration/styled';
import { ContentTitleSection } from 'views/ProtocolConfiguration/styled';
import { ContentItemFormSelect } from 'views/ProtocolConfiguration/styled';
import FormTooltip from 'components/Inputs/FormTooltip';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

const RecordTypeForm = () => {
  const history = useHistory();
  const [isUpdate, setIsUpdate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recordType, setRecordType] = useState(null);

  const { id } = useParams();
  const { reset, control, handleSubmit, errors, watch, setValue } = useForm();
  const { leadProcesses, getLeadProcesses } = useListLeadProcesses();
  const { salesProcesses, getSalesProcesses } = useListSalesProcesses();
  const { protocolProcesses, getProtocolProcesses } =
    useListProtocolProcesses();

  const isSlaActive = watch('is_sla_active');

  const selectedEntity = watch('entity');
  const options = useMemo(() => {
    const baseOptions = [
      { value: 'ACCOUNT', label: 'Conta' },
      { value: 'CONTACT', label: 'Contato' },
      { value: 'LEAD', label: 'Lead' },
      { value: 'OPPORTUNITY', label: 'Oportunidade' },
      { value: 'PROTOCOL', label: 'Protocolo' }
    ];

    // if (verifyRoles(['PRODUCT_CRM'])) {
    baseOptions.push({ value: 'PRODUCT', label: 'Produto' });
    // }

    return baseOptions;
  }, []);

  const getLeadStageProcessesOptions = useCallback(() => {
    if (!leadProcesses) return [];
    return leadProcesses.map((leadProcess) => ({
      value: leadProcess.id,
      label: leadProcess.name
    }));
  }, [leadProcesses]);

  const getSalesStageProcessesOptions = useCallback(() => {
    if (!salesProcesses) return [];
    return salesProcesses.map((salesProcess) => ({
      value: salesProcess.id,
      label: salesProcess.name
    }));
  }, [salesProcesses]);

  const getProtocolProcessesOptions = useCallback(() => {
    if (!protocolProcesses) return [];
    return protocolProcesses.map((protocolProcess) => ({
      value: protocolProcess.id,
      label: protocolProcess.name
    }));
  }, [protocolProcesses]);

  useEffect(() => {
    getLeadProcesses(1, {}, 500);
    getSalesProcesses(1, {}, 500);
    getProtocolProcesses(1, {}, 500);
  }, [getLeadProcesses, getSalesProcesses, getProtocolProcesses]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      RecordTypeService.get(id)
        .then((res) => {
          const recordTypeData = res.data.content;

          reset(recordTypeData);
          setRecordType(recordTypeData);
          setIsUpdate(true);
        })
        .catch((error) => {
          const errorMsg = error.response?.data?.message;
          toast.error(
            errorMsg || 'Erro ao buscar tipo de registro para edição'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id, reset]);

  const onSubmit = async (data) => {
    try {
      const formatedData = {
        ...data
      };

      isUpdate
        ? await RecordTypeService.update(id, formatedData)
        : await RecordTypeService.create(formatedData);

      const msg = isUpdate
        ? 'Tipo de registro alterado com sucesso'
        : 'Tipo de registro criado com sucesso';

      toast.success(msg);

      history.push('/admin/recordType');
    } catch (err) {
      const msg = err.response?.data?.message;
      toast.error(msg || 'Erro ao cadastrar tipo de registro', {
        autoClose: 3000,
        closeOnClick: true
      });
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/recordType');
  };

  return (
    <>
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/recordType"
        buttonTitle="Voltar para a pagina de tipos de registro"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-3">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Tipo de registro</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Nome*</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="name"
                            rules={{
                              required: 'Campo obrigatório',
                              maxLength: 255
                            }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="name"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">
                            Entidade*
                          </label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                isClearable={false}
                                dataOptions={options}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                value={props.value}
                                valueController={setValue}
                                fieldName="entity"
                                labelName="label"
                                valueName="value"
                                isDisabled={isUpdate}
                              />
                            )}
                            control={control}
                            name="entity"
                            rules={{
                              required: 'Campo obrigatório'
                            }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="entity"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        {((selectedEntity && selectedEntity === 'LEAD') ||
                          (recordType && recordType.entity === 'LEAD')) && (
                          <Col md="6">
                            <label className="form-control-label">
                              Processo*
                            </label>
                            <Controller
                              render={(props) => (
                                <FlexSelect
                                  isClearable={false}
                                  dataOptions={getLeadStageProcessesOptions()}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName="lead_process_id"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              control={control}
                              name="lead_process_id"
                              rules={{
                                required: 'Campo obrigatório'
                              }}
                              defaultValue=""
                            />
                            <ErrorMessage
                              errors={errors}
                              name="lead_process_id"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                        )}
                      </Row>
                      <Row className="mb-3">
                        {((selectedEntity &&
                          selectedEntity === 'OPPORTUNITY') ||
                          (recordType &&
                            recordType.entity === 'OPPORTUNITY')) && (
                          <Col md="6">
                            <label className="form-control-label">
                              Processo*
                            </label>
                            <Controller
                              render={(props) => (
                                <FlexSelect
                                  isClearable={false}
                                  dataOptions={getSalesStageProcessesOptions()}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName="sales_process_id"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              control={control}
                              name="sales_process_id"
                              rules={{
                                required: 'Campo obrigatório'
                              }}
                              defaultValue=""
                            />
                            <ErrorMessage
                              errors={errors}
                              name="sales_process_id"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </Col>
                        )}
                      </Row>
                      <Row className="mb-3">
                        {((selectedEntity && selectedEntity === 'PROTOCOL') ||
                          (recordType && recordType.entity === 'PROTOCOL')) && (
                          <>
                            <Col md="6">
                              <label className="form-control-label">
                                Processo de Atendimento*
                              </label>
                              <Controller
                                render={(props) => (
                                  <FlexSelect
                                    isClearable={false}
                                    dataOptions={getProtocolProcessesOptions()}
                                    isMulti={false}
                                    closeMenuOnSelect={true}
                                    value={props.value}
                                    valueController={setValue}
                                    fieldName="protocol_process_id"
                                    labelName="label"
                                    valueName="value"
                                  />
                                )}
                                control={control}
                                name="protocol_process_id"
                                rules={{
                                  required: 'Campo obrigatório'
                                }}
                                defaultValue=""
                              />
                              <ErrorMessage
                                errors={errors}
                                name="protocol_process_id"
                                render={({ message }) =>
                                  errorFormMessage(message)
                                }
                              />
                            </Col>
                            <Col
                              md="5"
                              className="pl-5 mt-3"
                              style={{
                                display: 'flex',
                                flexDirection: 'column'
                              }}
                            >
                              <label className="form-control-label">
                                Habilitar SLA
                              </label>
                              <Controller
                                name={'is_sla_active'}
                                control={control}
                                defaultValue={false}
                                render={(props) => {
                                  return (
                                    <>
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                            setValue(
                                              'is_sla_active',
                                              e.target.checked
                                            );
                                          }}
                                          checked={props.value}
                                        />
                                        Habilitar SLA no tipo de registro
                                      </label>
                                    </>
                                  );
                                }}
                              />
                            </Col>
                            {isSlaActive && (
                              <>
                                <Col
                                  md="6"
                                  className="pl-5 mt-3"
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}
                                >
                                  <label className="form-control-label">
                                    Envio de notificação
                                  </label>
                                  <Controller
                                    name="should_send_notification_to_supervisor"
                                    defaultValue={false}
                                    control={control}
                                    render={(props) => (
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                          }}
                                          checked={props.value}
                                        />
                                        Habilitar notificação para supervisor
                                        <FormTooltip text="Ao marcar essa opção, O supervisor do responsável atual do protocolo será notificado na plataforma do flexUC." />
                                      </label>
                                    )}
                                  />
                                  <Controller
                                    name="should_send_notification_to_attendant"
                                    defaultValue={false}
                                    control={control}
                                    render={(props) => (
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                          }}
                                          checked={props.value}
                                        />
                                        Habilitar notificação para atendente
                                        <FormTooltip text="Ao marcar essa opção, O responsável atual do protocolo será notificado na plataforma do flexUC." />
                                      </label>
                                    )}
                                  />
                                  <Controller
                                    name="should_send_notification_to_client"
                                    defaultValue={false}
                                    control={control}
                                    render={(props) => (
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                          }}
                                          checked={props.value}
                                        />
                                        Habilitar notificação para cliente
                                        <FormTooltip text="Ao marcar essa opção, O cliente do protocolo será notificado por email." />
                                      </label>
                                    )}
                                  />
                                </Col>
                                <Col
                                  md="5"
                                  className="pl-5 mt-3"
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}
                                >
                                  <label className="form-control-label">
                                    Nivel da notificação do Cliente
                                  </label>
                                  <Controller
                                    name="movement_alert"
                                    defaultValue={false}
                                    control={control}
                                    render={(props) => (
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                          }}
                                          checked={props.value}
                                        />
                                        Alerta de movimentação
                                        <FormTooltip text="Ao marcar essa opção, O usuário será notificado quando a conta, motivo/submotivo ou status são alterados." />
                                      </label>
                                    )}
                                  />
                                  <Controller
                                    name="sla_time_alert"
                                    defaultValue={false}
                                    control={control}
                                    render={(props) => (
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                          }}
                                          checked={props.value}
                                        />
                                        Tempo de SLA
                                        <FormTooltip text="Ao marcar essa opção, O usuário será notificado a cada 25% do tempo de solução do SLA." />
                                      </label>
                                    )}
                                  />
                                  <Controller
                                    name="opening_or_closing_alert"
                                    defaultValue={false}
                                    control={control}
                                    render={(props) => (
                                      <label className="form-control-label">
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                          }}
                                          checked={props.value}
                                        />
                                        Abertura/Encerramento do protocolo
                                        <FormTooltip text="Ao marcar essa opção, O usuário será notificado quando o protocolo for aberto ou encerrado." />
                                      </label>
                                    )}
                                  />
                                </Col>
                              </>
                            )}
                          </>
                        )}
                      </Row>

                      {((selectedEntity && selectedEntity === 'LEAD') ||
                        (recordType && recordType.entity === 'LEAD')) && (
                        <>
                          <ContainerForm>
                            <ContentTitleSection>
                              <Row>
                                <label>
                                  Configuração de criação de novos leads
                                </label>
                              </Row>
                            </ContentTitleSection>
                            <ContentItemFormSelect>
                              <Row>
                                <Col md="6">
                                  <Controller
                                    name="accepts_duplicates_creation"
                                    control={control}
                                    defaultValue={false}
                                    render={(props) => (
                                      <>
                                        <Input
                                          type="checkbox"
                                          onChange={(e) => {
                                            props.onChange(e.target.checked);
                                            setValue(
                                              'accepts_duplicates_creation',
                                              e.target.checked
                                            );
                                          }}
                                          id="accepts_duplicates_creation"
                                          checked={props.value}
                                        />

                                        <label
                                          className="form-control-label"
                                          htmlFor="accepts_duplicates_creation"
                                        >
                                          Permitir importação de leads com
                                          telefone e email já cadastrados
                                        </label>
                                      </>
                                    )}
                                  />
                                </Col>
                              </Row>
                            </ContentItemFormSelect>
                          </ContainerForm>
                        </>
                      )}

                      <hr />
                      <ReactButton btnColor="confirmation" type="submit">
                        Salvar
                      </ReactButton>

                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};

export { RecordTypeForm };
